import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Send, User, Smartphone, Briefcase, MessageSquare, Loader2 } from "lucide-react";
import SuccessModal from "./SuccessModal";

interface WorkWithUsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const WorkWithUsDialog = ({ isOpen, onClose }: WorkWithUsDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    
    try {
      // Usando o e-mail principal para o FormSubmit e os outros via hidden _cc se necessário, 
      // ou mantendo o formato que eles sugerem para múltiplos destinatários.
      // O erro do usuário sugere que a string longa no action falhou.
      // Vou usar o primeiro e-mail como principal.
      const response = await fetch("https://formsubmit.co/ajax/jldespachantenautico@gmail.com", {
        method: "POST",
        body: formData
      });

      if (response.ok) {
        setShowSuccess(true);
      } else {
        alert("Ocorreu um erro ao enviar. Por favor, tente novamente.");
      }
    } catch (error) {
      console.error("Erro no envio:", error);
      alert("Erro de conexão. Verifique sua internet.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseAll = () => {
    setShowSuccess(false);
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[500px] bg-primary border-gold/20 text-primary-foreground max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading text-gold">Faça parte da nossa equipe</DialogTitle>
            <DialogDescription className="text-primary-foreground/60">
              Preencha os dados abaixo e envie seu currículo para análise.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5 py-4">
            <input type="hidden" name="_subject" value="Novo Candidato - Trabalhe Conosco Site JL" />
            <input type="hidden" name="_template" value="table" />
            <input type="hidden" name="_captcha" value="false" />
            
            {/* Adicionando os outros e-mails via _cc para garantir o recebimento de todos */}
            <input type="hidden" name="_cc" value="lucasdasilvamaximorosa@gmail.com,vinitietjen@gmail.com,souzasandro19701@gmail.com,irushlab@gmail.com" />

            <div className="space-y-2">
              <Label htmlFor="recr-name" className="flex items-center gap-2 text-[10px] font-bold text-gold uppercase tracking-widest"><User className="w-3 h-3"/> Nome Completo</Label>
              <Input id="recr-name" name="Nome" placeholder="Seu nome" required className="bg-white/5 border-white/10 h-12" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recr-whatsapp" className="flex items-center gap-2 text-[10px] font-bold text-gold uppercase tracking-widest"><Smartphone className="w-3 h-3"/> WhatsApp</Label>
              <Input id="recr-whatsapp" name="WhatsApp" placeholder="(47) 99999-9999" required className="bg-white/5 border-white/10 h-12" />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-[10px] font-bold text-gold uppercase tracking-widest"><Briefcase className="w-3 h-3"/> Vaga de Interesse</Label>
              <Select name="Vaga de Interesse" required>
                <SelectTrigger className="bg-white/5 border-white/10 h-12 text-white">
                  <SelectValue placeholder="Selecione a vaga" />
                </SelectTrigger>
                <SelectContent className="bg-navy-deep border-gold/20 text-white">
                  <SelectItem value="Instrutor Náutico">Instrutor Náutico</SelectItem>
                  <SelectItem value="Despachante">Despachante / Administrativo</SelectItem>
                  <SelectItem value="Vendas">Consultor de Vendas</SelectItem>
                  <SelectItem value="Marinheiro">Marinheiro / Operacional</SelectItem>
                  <SelectItem value="Outros">Outros</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="recr-msg" className="flex items-center gap-2 text-[10px] font-bold text-gold uppercase tracking-widest"><MessageSquare className="w-3 h-3"/> Breve Resumo / Experiência</Label>
              <Textarea id="recr-msg" name="Experiência" placeholder="Conte um pouco sobre sua trajetória..." className="bg-white/5 border-white/10 h-24" />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-[10px] font-bold text-gold uppercase tracking-widest"><Upload className="w-3 h-3"/> Anexar Currículo (PDF/DOCX)</Label>
              <Input type="file" name="Currículo" required className="bg-white/5 border-white/10 file:bg-gold file:text-primary file:border-0 file:rounded-md file:px-2 file:py-1 file:mr-2 file:text-xs file:font-bold" />
              <p className="text-[10px] text-primary-foreground/40 italic">Tamanho máximo: 5MB</p>
            </div>

            <div className="pt-4 flex gap-3">
              <Button 
                type="button" 
                variant="ghost" 
                onClick={onClose} 
                disabled={isSubmitting}
                className="flex-1 border border-white/30 text-white hover:bg-white/10 hover:text-white transition-all font-medium"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1 btn-gold font-bold flex gap-2">
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar Currículo"} <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <SuccessModal 
        isOpen={showSuccess} 
        onClose={handleCloseAll} 
        message="Seu currículo foi enviado com sucesso para nossa equipe de RH."
      />
    </>
  );
};

export default WorkWithUsDialog;
